import type {
  BookingDetailsParticipant,
  BookingDetailsParticipantExtra,
} from '../../domain/types'
import type {
  BookingDetailsParticipantAddressAPIResponse,
  BookingDetailsParticipantAPIResponse,
  BookingDetailsServiceAPIResponse,
} from '../types'
import { getParticipantType } from '../../domain/participantType'

function mapParticipantAddress(
  participantAddress?: BookingDetailsParticipantAddressAPIResponse,
): BookingDetailsParticipant['address'] {
  return {
    city: participantAddress?.city || '',
    postalCode: participantAddress?.postalCode || '',
    street: participantAddress?.streetName || '',
    houseNumber: participantAddress?.houseNumber || '',
    houseNumberAddition: participantAddress?.houseNumberAddition || '',
  }
}

function buildParticipantExtrasMap(
  participants: BookingDetailsParticipantAPIResponse[],
  services: BookingDetailsServiceAPIResponse[],
): Map<string, BookingDetailsParticipantExtra[]> {
  const participantGroupsMap = new Map<string, Map<string, BookingDetailsParticipantExtra>>()

  for (const service of services) {
    const description = service.description?.trim()
    const label = service.title?.trim() || service.type?.trim() || ''
    const participantIds = service.participantIds?.map(String)
    const hasValidParticipantAssignment = service.quantity !== undefined
      && service.quantity === participantIds?.length

    if (description && label && participantIds && hasValidParticipantAssignment) {
      const icon = service.icon?.trim() || ''

      for (const participantId of participantIds) {
        if (!participantGroupsMap.has(participantId)) {
          participantGroupsMap.set(participantId, new Map())
        }

        const groups = participantGroupsMap.get(participantId)!

        if (!groups.has(label)) {
          groups.set(label, { label, descriptions: [], icon })
        }

        groups.get(label)!.descriptions.push(description)
      }
    }
  }

  const result = new Map<string, BookingDetailsParticipantExtra[]>()
  participantGroupsMap.forEach((groups, participantId) => {
    result.set(participantId, Array.from(groups.values()))
  })
  return result
}

export function participantsMapper(
  participants: BookingDetailsParticipantAPIResponse[] | undefined = [],
  services: BookingDetailsServiceAPIResponse[] | undefined = [],
): Record<string, BookingDetailsParticipant> {
  const extrasMap = buildParticipantExtrasMap(participants, services)

  return participants.reduce<Record<string, BookingDetailsParticipant>>((acc, participant) => {
    const type = getParticipantType({
      type: participant.isMainBooker ? 'MainBooker' : '',
      title: participant.title || '',
    })
    const participantId = String(participant.id ?? '')

    acc[participantId] = {
      id: participantId,
      title: participant.title || '',
      firstName: participant.firstName || '',
      lastName: participant.lastName || '',
      birthDate: participant.birthDate || '',
      email: participant.emailAddress || '',
      phoneNumbers: participant.phoneNumbers ?? [],
      emergencyPhoneNumbers: participant.emergencyPhoneNumbers ?? [],
      address: mapParticipantAddress(participant.address),
      type,
      isCancelled: participant.isCancelled ?? false,
      extras: extrasMap.get(participantId) ?? [],
    }

    return acc
  }, {})
}
